import React, { useState } from "react";
import PageHeader from "@/components/PageHeader";
import EntityDialog from "@/components/EntityDialog";
import { useResource } from "@/lib/useResource";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, Plus, Search } from "lucide-react";

export default function EntityListPage({
    testId,
    title,
    description,
    resourcePath,
    fields,
    columns,
    addLabel = "Add",
    emptyLabel = "Nothing here yet",
    summaryCards,
}) {
    const { items, summary, loading, q, setQ, create, update, remove } = useResource(resourcePath);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [toDelete, setToDelete] = useState(null);

    const openCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };

    return (
        <div data-testid={testId}>
            <PageHeader
                title={title}
                description={description}
                actions={
                    <Button size="sm" onClick={openCreate} data-testid={`${testId}-add`}>
                        <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> {addLabel}
                    </Button>
                }
            />
            <div className="p-8 space-y-4 max-w-[1400px]">
                {summary && summaryCards && (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        {summaryCards(summary).map((c) => (
                            <div
                                key={c.label}
                                className="rounded-md border border-zinc-200 bg-white px-4 py-3"
                                data-testid={`${testId}-summary-${c.key}`}
                            >
                                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                    {c.label}
                                </div>
                                <div className="mt-1 font-display text-xl font-semibold">{c.value}</div>
                                {c.hint && <div className="mt-0.5 text-[11px] text-zinc-500">{c.hint}</div>}
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search
                            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400"
                            strokeWidth={1.5}
                        />
                        <Input
                            placeholder="Search…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-8 h-9"
                            data-testid={`${testId}-search`}
                        />
                    </div>
                    <div className="ml-auto text-xs text-zinc-500 font-mono">{items.length} items</div>
                </div>

                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-zinc-50/60">
                                {columns.map((c) => (
                                    <TableHead
                                        key={c.key}
                                        className={`text-[10px] uppercase tracking-widest text-zinc-500 ${
                                            c.align === "right" ? "text-right" : ""
                                        }`}
                                    >
                                        {c.label}
                                    </TableHead>
                                ))}
                                <TableHead className="w-10" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length + 1}
                                        className="py-12 text-center text-sm text-zinc-500"
                                    >
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length + 1}
                                        className="py-12 text-center text-sm text-zinc-500"
                                    >
                                        {q ? "No matches." : emptyLabel}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row) => (
                                    <TableRow key={row.id} data-testid={`${testId}-row-${row.id}`}>
                                        {columns.map((c) => (
                                            <TableCell
                                                key={c.key}
                                                className={c.align === "right" ? "text-right font-mono text-xs" : ""}
                                            >
                                                {c.render ? c.render(row) : row[c.key] ?? "—"}
                                            </TableCell>
                                        ))}
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => openEdit(row)}>
                                                        Edit
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className="text-destructive focus:text-destructive"
                                                        onClick={() => setToDelete(row)}
                                                    >
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <EntityDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                title={editing ? "Edit" : addLabel}
                description={editing ? "Update this record." : "Create a new record."}
                fields={fields}
                initial={editing}
                testId={`${testId}-dialog`}
                onSubmit={async (payload) => {
                    if (editing) return update(editing.id, payload);
                    return create(payload);
                }}
            />

            <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (toDelete) remove(toDelete.id);
                                setToDelete(null);
                            }}
                            data-testid={`${testId}-confirm-delete`}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export function StatusBadge({ active }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-xs text-zinc-700">
            <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-zinc-300"}`} />
            {active ? "Active" : "Inactive"}
        </span>
    );
}

export function Money({ value }) {
    if (value == null || value === "") return "—";
    return <span className="font-mono">₹{Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>;
}
